import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Heart, MessageCircle, Bookmark, Send, X,
  MoreVertical, Trash2, Flag, AlertTriangle,
  Smile, ExternalLink
} from 'lucide-react';
import EmojiPicker from 'emoji-picker-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from './AuthContext.jsx';

import { BACKEND_URL as GREENWICH_BACKEND_URL } from '../utils/network.js';
import { localizeMessage } from '../utils/toast.js';
const API_BASE_URL = `${GREENWICH_BACKEND_URL}/api`;
const BASE_URL     = `${GREENWICH_BACKEND_URL}`;

const handleImageError = (e) => {
  e.target.onerror = null;
  e.target.src = '/nkarner/20260110_1932_Image Generation_remix_01kem8h20rewa81f8h55ncqmm9 (2).png';
};

const getImageUrl = (imagePath) => {
  if (!imagePath || imagePath === 'null' || imagePath === 'undefined')
    return '/nkarner/20260110_1932_Image Generation_remix_01kem8h20rewa81f8h55ncqmm9 (2).png';
  if (imagePath.startsWith('http')) return imagePath;
  if (imagePath.includes('uploads') || imagePath.includes('nkarner')) {
    const clean = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
    return `${BASE_URL}${clean}`;
  }
  return '/nkarner/20260110_1932_Image Generation_remix_01kem8h20rewa81f8h55ncqmm9 (2).png';
};

const formatDate = (date, t) => {
  if (!date) return '';
  try {
    const d    = new Date(date);
    if (isNaN(d.getTime())) return '';
    const now  = new Date();
    const diff = now - d;
    const m    = Math.floor(diff / 60000);
    const h    = Math.floor(diff / 3600000);
    const day  = Math.floor(diff / 86400000);
    if (m  < 1)  return t ? t('postModal.justNow') : 'Just now';
    if (m  < 60) return t ? t('postModal.minutesAgo', { m }) : `${m}m ago`;
    if (h  < 24) return t ? t('postModal.hoursAgo', { h }) : `${h}h ago`;
    if (day < 7) return t ? t('postModal.daysAgo', { day }) : `${day}d ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch { return ''; }
};

const apiRequest = async (endpoint, options = {}) => {
  const token = localStorage.getItem('token');
  if (!token) throw new Error('No auth token');
  const headers = {
    'Authorization': `Bearer ${token}`,
    ...(options.body && typeof options.body === 'string' ? { 'Content-Type': 'application/json' } : {}),
    ...options.headers
  };
  const res = await fetch(`${API_BASE_URL}${endpoint}`, { ...options, headers });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || err.message || `HTTP ${res.status}`);
  }
  return res.json();
};

// ── ImageCarousel ──────────────────────────────────────────────────────────
const ImageCarousel = ({ images = [] }) => {
  const [idx, setIdx]         = useState(0);
  const [hovered, setHovered] = useState(false);
  if (!images.length) return null;
  const prev = (e) => { e.stopPropagation(); setIdx(i => i === 0 ? images.length - 1 : i - 1); };
  const next = (e) => { e.stopPropagation(); setIdx(i => i === images.length - 1 ? 0 : i + 1); };
  return (
    <div className="image-carousel-container" style={{ position: 'relative' }}
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
      <div style={{ position: 'relative' }}>
        <img src={getImageUrl(images[idx])} alt={`img-${idx}`} onError={handleImageError}
          style={{ width: '100%', maxHeight: 420, objectFit: 'contain', backgroundColor: '#f5f5f5', borderRadius: 8, display: 'block' }} />
      </div>
      {images.length > 1 && (
        <>
          <div style={{ display: hovered ? 'flex' : 'none', position: 'absolute', top: '50%', left: 0, right: 0, transform: 'translateY(-50%)', justifyContent: 'space-between', pointerEvents: 'none', zIndex: 2 }}>
            <button className="carousel-btn clay-press clay-focus-glow" onClick={prev} style={{ pointerEvents: 'auto', marginLeft: 5 }}>{'<'}</button>
            <button className="carousel-btn clay-press clay-focus-glow" onClick={next} style={{ pointerEvents: 'auto', marginRight: 5 }}>{'>'}</button>
          </div>
          <div style={{ position: 'absolute', bottom: 14, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 10, zIndex: 2 }}>
            {images.map((_, i) => (
              <button key={i} onClick={(e) => { e.stopPropagation(); setIdx(i); }}
                className={`carousel-dot ${i === idx ? 'active' : ''}`} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// ── CommentItem ────────────────────────────────────────────────────────────
const CommentItem = ({ comment, depth = 0, onLike, onReport, onDeleteClick,
  currentUserId, postId, replyInputs, setReplyInputs,
  handleCommentReply, activeEmojiComment, setActiveEmojiComment }) => {
  const { t } = useTranslation();
  const [showOpts, setShowOpts] = useState(false);
  const optionsRef = useRef(null);
  useEffect(() => {
    const h = (e) => { if (optionsRef.current && !optionsRef.current.contains(e.target)) setShowOpts(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const isLiked = comment.likes?.includes(currentUserId) || false;
  return (
    <div className="comment-item" style={{ marginLeft: depth > 0 ? 32 : 0, marginTop: depth > 0 ? 8 : 0, position: 'relative' }}>
      {depth > 0 && <div style={{ position: 'absolute', left: -16, top: 24, bottom: 0, width: 2, backgroundColor: '#e0e0e0' }} />}
      <div className="comment" style={{ display: 'flex', gap: 8 }}>
        <img src={getImageUrl(comment.author?.profileImage)} alt={comment.author?.name}
          className="comment-avatar" style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} onError={handleImageError} />
        <div className="comment-content" style={{ flex: 1 }}>
          <div className="comment-header" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span className="comment-author-name" style={{ fontWeight: 600, fontSize: 14 }}>{comment.author?.name || t('postModal.unknown')}</span>
            <span className="comment-time" style={{ fontSize: 12, color: '#65676b' }}>{formatDate(comment.createdAt, t)}</span>
          </div>
          <p className="comment-text" style={{ margin: '4px 0', fontSize: 14, lineHeight: 1.4 }}>{comment.content || ''}</p>
          <div className="comment-actions" style={{ display: 'flex', gap: 12, marginTop: 4 }}>
            <button className={`comment-action-btn like-btn ${isLiked ? 'liked' : ''}`} onClick={() => onLike(comment._id)}
              style={{ background: 'none', border: 'none', display: 'flex', alignItems: 'center', gap: 4, padding: '2px 4px', cursor: 'pointer', fontSize: 12, color: isLiked ? '#ef4444' : '#65676b' }}>
              <Heart size={12} fill={isLiked ? '#ef4444' : 'none'} color={isLiked ? '#ef4444' : '#65676b'} />
              <span>{comment.likes?.length || 0}</span>
            </button>
            {depth < 10 && (
              <button onClick={() => setReplyInputs({ ...replyInputs, [comment._id]: `@${comment.author?.name || t('postModal.user')} ` })}
                style={{ background: 'none', border: 'none', display: 'flex', alignItems: 'center', gap: 4, padding: '2px 4px', cursor: 'pointer', fontSize: 12, color: '#65676b' }}>
                <span>{t('postModal.reply')}</span>
              </button>
            )}
            <div ref={optionsRef} style={{ position: 'relative' }}>
              <button onClick={() => setShowOpts(!showOpts)}
                style={{ background: 'none', border: 'none', display: 'flex', alignItems: 'center', padding: '2px 4px', cursor: 'pointer', color: '#65676b' }}>
                <MoreVertical size={12} />
              </button>
              {showOpts && (
                <div style={{ position: 'absolute', top: '100%', right: 0, background: 'white', border: '1px solid #e0e0e0', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', zIndex: 100, minWidth: 140 }}>
                  <button onClick={() => { onReport && onReport(comment._id); setShowOpts(false); }}
                    style={{ width: '100%', padding: '8px 12px', border: 'none', background: 'none', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: '#65676b' }}>
                    <Flag size={14} /><span>{t('postModal.report')}</span>
                  </button>
                  {comment.author?._id === currentUserId && (
                    <button onClick={() => { onDeleteClick && onDeleteClick(comment._id); setShowOpts(false); }}
                      style={{ width: '100%', padding: '8px 12px', border: 'none', background: 'none', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: '#ef4444' }}>
                      <Trash2 size={14} /><span>{t('postModal.delete')}</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
          {replyInputs[comment._id] !== undefined && (
            <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center', position: 'relative' }}>
              <div style={{ flex: 1, position: 'relative' }}>
                <input type="text" placeholder={t('postModal.writeReply')}
                  value={replyInputs[comment._id] || ''}
                  onChange={(e) => setReplyInputs({ ...replyInputs, [comment._id]: e.target.value })}
                  onKeyPress={(e) => e.key === 'Enter' && handleCommentReply(comment._id)}
                  data-comment-id={comment._id}
                  style={{ width: '100%', padding: '8px 40px 8px 12px', border: '1px solid #e0e0e0', borderRadius: 20, fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                <button onClick={(e) => { e.stopPropagation(); setActiveEmojiComment(activeEmojiComment === comment._id ? null : comment._id); }}
                  style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#65676b' }}>
                  <Smile size={16} />
                </button>
              </div>
              <button onClick={() => handleCommentReply(comment._id)} disabled={!replyInputs[comment._id]?.trim()}
                style={{ background: '#32614d', color: 'white', border: 'none', borderRadius: '50%', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, opacity: replyInputs[comment._id]?.trim() ? 1 : 0.5 }}>
                <Send size={14} />
              </button>
              {activeEmojiComment === comment._id && (
                <div style={{ position: 'absolute', bottom: '110%', left: 0, zIndex: 9999 }}>
                  <EmojiPicker onEmojiClick={(d) => { setReplyInputs({ ...replyInputs, [comment._id]: (replyInputs[comment._id] || '') + d.emoji }); setActiveEmojiComment(null); }}
                    disableAutoFocus native height={320} width={280} />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Main PostModal ─────────────────────────────────────────────────────────
export default function PostModal({ postId, isOpen, onClose }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();

  const [post,           setPost]           = useState(null);
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState(null);
  const [successMsg,     setSuccessMsg]     = useState(null);
  const [isLiking,       setIsLiking]       = useState(false);
  const [isSaving,       setIsSaving]       = useState(false);
  const [isCommenting,   setIsCommenting]   = useState(false);
  const [commentInput,   setCommentInput]   = useState('');
  const [replyInputs,    setReplyInputs]    = useState({});
  const [activeEmoji,    setActiveEmoji]    = useState(null);
  const [showComments,   setShowComments]   = useState(false);
  const [showDelPost,    setShowDelPost]    = useState(false);
  const [showReport,     setShowReport]     = useState(false);
  const [reportTarget,   setReportTarget]   = useState({ type: '', id: '', reason: '' });
  const [commentToDel,   setCommentToDel]   = useState(null);
  const [showDelComment, setShowDelComment] = useState(false);

  const scrollRef = useRef(null);
  const userId    = user?._id || user?.id || null;

  useEffect(() => {
    if (isOpen) { document.body.style.overflow = 'hidden'; }
    else { document.body.style.overflow = ''; }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen || !postId) return;
    setPost(null); setLoading(true); setError(null);
    (async () => {
      try {
        const data = await apiRequest(`/posts/${postId}`);
        const p    = data.post || data;
        const auth = p.author || p.user || {};
        const normalized = {
          _id:       p._id || p.id,
          content:   p.content || '',
          images:    p.images || (p.image ? [p.image] : []),
          createdAt: p.createdAt || new Date().toISOString(),
          author: {
            _id:              auth._id || auth.id || 'unknown',
            organizationName: auth.organizationName || auth.name || t('postModal.unknown'),
            name:             auth.name || auth.username || t('postModal.unknown'),
            profileImage:     auth.profileImage || '',
            userType:         auth.userType || 'player',
            isVerified:       auth.isVerified || false,
          },
          likes:    Array.isArray(p.likes)    ? p.likes    : [],
          saves:    Array.isArray(p.saves)    ? p.saves    : [],
          comments: Array.isArray(p.comments) ? p.comments : [],
        };
        setPost(normalized);
        try {
          const cr   = await apiRequest(`/posts/${postId}/comments`);
          const cmts = Array.isArray(cr) ? cr : cr.comments || [];
          if (cmts.length > 0) setPost(prev => prev ? { ...prev, comments: cmts } : prev);
        } catch {}
      } catch (err) {
        setError(err.message || t('postModal.failedLoadPost'));
      } finally {
        setLoading(false);
      }
    })();
  }, [isOpen, postId]);

  useEffect(() => {
    if (successMsg) { const t = setTimeout(() => setSuccessMsg(null), 3000); return () => clearTimeout(t); }
  }, [successMsg]);

  const isLiked     = () => post?.likes?.some(l => (typeof l === 'string' ? l : l._id) === userId) || false;
  const isPostSaved = () => post?.saves?.some(s => (typeof s === 'string' ? s : s._id) === userId) || false;

  const handleLike = async () => {
    if (!userId || isLiking || !post) return;
    const was = isLiked();
    setIsLiking(true);
    setPost(p => ({ ...p, likes: was ? p.likes.filter(l => (typeof l === 'string' ? l : l._id) !== userId) : [...p.likes, userId] }));
    try { await apiRequest(`/posts/${postId}/like`, { method: 'POST' }); }
    catch { setPost(p => ({ ...p, likes: was ? [...p.likes, userId] : p.likes.filter(l => (typeof l === 'string' ? l : l._id) !== userId) })); }
    finally { setIsLiking(false); }
  };

  const handleSave = async () => {
    if (!userId || isSaving || !post) return;
    const was = isPostSaved();
    setIsSaving(true);
    setPost(p => ({ ...p, saves: was ? p.saves.filter(s => (typeof s === 'string' ? s : s._id) !== userId) : [...p.saves, userId] }));
    try { await apiRequest(`/posts/${postId}/save`, { method: 'POST', body: JSON.stringify({ action: was ? 'unsave' : 'save' }) }); }
    catch {}
    finally { setIsSaving(false); }
  };

  const handleComment = async (parentCommentId = null, content = null) => {
    if (!userId) return;
    const text = content || commentInput.trim();
    if (!text) return;
    setIsCommenting(true);
    const tempId = `temp_${Date.now()}`;
    const newC = {
      _id: tempId, content: text, parentCommentId: parentCommentId || null,
      author: { _id: userId, name: user?.name || t('postModal.me'), profileImage: user?.profileImage },
      createdAt: new Date().toISOString(), likes: []
    };
    setPost(p => ({ ...p, comments: [newC, ...(p.comments || [])] }));
    if (!content) setCommentInput('');
    try {
      const res = await apiRequest(`/posts/${postId}/comments`, { method: 'POST', body: JSON.stringify({ content: text, parentCommentId: parentCommentId || null }) });
      if (res.comment) setPost(p => ({ ...p, comments: p.comments.map(c => c._id === tempId ? res.comment : c) }));
      setSuccessMsg(parentCommentId ? t('postModal.replyPosted') : t('postModal.commentAdded'));
      if (parentCommentId) setReplyInputs(prev => { const n = { ...prev }; delete n[parentCommentId]; return n; });
    } catch {
      setPost(p => ({ ...p, comments: p.comments.filter(c => c._id !== tempId) }));
    } finally { setIsCommenting(false); }
  };

  const handleCommentReply = async (cId) => { const t = replyInputs[cId]?.trim(); if (t) await handleComment(cId, t); };

  const handleCommentLike = async (cId) => {
    if (!userId) return;
    setPost(p => ({ ...p, comments: p.comments.map(c => {
      if ((c._id || c.id) !== cId) return c;
      const liked = c.likes?.includes(userId);
      return { ...c, likes: liked ? (c.likes || []).filter(id => id !== userId) : [...(c.likes || []), userId] };
    })}));
    try { await apiRequest(`/comments/${cId}/like`, { method: 'POST' }); } catch {}
  };

  const handleDeletePost = async () => {
    try {
      await apiRequest(`/posts/${postId}`, { method: 'DELETE' });
      setSuccessMsg(t('postModal.postDeleted'));
      setTimeout(() => onClose(), 800);
    } catch {}
  };

  const handleDeleteComment = async (cId) => {
    setPost(p => ({ ...p, comments: p.comments.filter(c => (c._id || c.id) !== cId) }));
    try { await apiRequest(`/comments/${cId}`, { method: 'DELETE' }); } catch {}
    setSuccessMsg(t('postModal.commentDeleted'));
  };

  const handleReport = async (data) => {
    try { await apiRequest('/reports', { method: 'POST', body: JSON.stringify(data) }); setSuccessMsg(t('postModal.reportSubmitted')); } catch {}
  };

  const buildCommentTree = (comments) => {
    if (!comments || !Array.isArray(comments)) return [];
    const map = {}; const root = [];
    comments.forEach(c => { if (c?._id) map[c._id] = { ...c, children: [] }; });
    comments.forEach(c => { if (c?._id) { if (c.parentCommentId && map[c.parentCommentId]) map[c.parentCommentId].children.push(map[c._id]); else root.push(map[c._id]); } });
    return root;
  };

  const renderTree = (comments, depth = 0) => {
    if (!comments?.length) return null;
    return comments.map(c => (
      <React.Fragment key={`c-${c._id}`}>
        <CommentItem comment={c} depth={depth}
          onLike={handleCommentLike}
          onReport={(cId) => { setReportTarget({ type: 'comment', id: cId, reason: '' }); setShowReport(true); }}
          onDeleteClick={(cId) => { setCommentToDel(cId); setShowDelComment(true); }}
          currentUserId={userId} postId={postId}
          replyInputs={replyInputs} setReplyInputs={setReplyInputs}
          handleCommentReply={handleCommentReply}
          activeEmojiComment={activeEmoji} setActiveEmojiComment={setActiveEmoji}
        />
        {c.children?.length > 0 && <div className="comment-children">{renderTree(c.children, depth + 1)}</div>}
      </React.Fragment>
    ));
  };

  if (!isOpen) return null;

  const commentTree = post ? buildCommentTree(post.comments) : [];
  const hasImages   = post?.images?.length > 0;

  return (
    <>
      <style>{`
        .pm-overlay {
          position: fixed; inset: 0; z-index: 9998;
          background: rgba(0,0,0,0.72);
          display: flex; align-items: center; justify-content: center;
          padding: 20px;
          animation: pmFadeIn .18s ease;
        }
        @keyframes pmFadeIn { from { opacity: 0 } to { opacity: 1 } }
        .pm-modal {
          background: #fff; border-radius: 16px; width: 100%;
          max-width: 720px; max-height: 92vh;
          display: flex; flex-direction: column; overflow: hidden;
          box-shadow: 0 24px 64px rgba(0,0,0,0.35);
          animation: pmSlideUp .22s ease; position: relative;
        }
        @keyframes pmSlideUp { from { transform: translateY(20px); opacity: 0 } to { transform: translateY(0); opacity: 1 } }
        .pm-close {
          position: absolute; top: 12px; right: 12px; z-index: 10;
          width: 34px; height: 34px; border-radius: 50%;
          background: rgba(0,0,0,0.07); border: none;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer; transition: background .15s;
        }
        .pm-close:hover { background: rgba(0,0,0,0.15); }
        .pm-scroll {
          overflow-y: auto; flex: 1; padding: 16px 20px;
        }
        .pm-scroll::-webkit-scrollbar { width: 5px; }
        .pm-scroll::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 10px; }
        .pm-status {
          padding: 8px 16px; font-size: 13px; font-weight: 500;
          border-radius: 8px; margin: 0 0 8px;
          animation: pmFadeIn .2s ease;
        }
        .pm-status.success { background: #f0fdf4; color: #16a34a; }
        .pm-status.error   { background: #fef2f2; color: #dc2626; }
        .pm-spinner {
          display: flex; align-items: center; justify-content: center;
          padding: 60px 0; color: #65676b; gap: 10px;
        }
        .pm-dot-spin {
          width: 28px; height: 28px;
          border: 3px solid rgba(50,97,77,.15); border-top-color: #32614d;
          border-radius: 50%; animation: pmSpin .7s linear infinite;
        }
        @keyframes pmSpin { to { transform: rotate(360deg) } }

        /* ── Action buttons spaced evenly ── */
        .pm-actions {
          display: flex;
          align-items: center;
          justify-content: space-around;
          padding: 8px 0;
          margin-top: 12px;
          border-top: 1px solid #f0f0f0;
          border-bottom: 1px solid #f0f0f0;
        }
        .pm-action-btn {
          display: flex; align-items: center; gap: 8px;
          background: none; border: none; cursor: pointer;
          font-size: 14px; font-weight: 600;
          padding: 10px 20px;
          border-radius: 8px;
          color: #65676b;
          transition: background .15s, color .15s;
          min-width: 80px; justify-content: center;
        }
        .pm-action-btn:hover { background: rgba(0,0,0,0.05); }
        .pm-action-btn.active { color: #32614d; }
      `}</style>

      <div className="pm-overlay clay-modal-backdrop" onClick={onClose}>
        <div className="pm-modal clay-modal-enter" onClick={(e) => e.stopPropagation()}>

      

          <div className="pm-scroll" ref={scrollRef}>
            {successMsg && <div className="pm-status success">{successMsg}</div>}
            {error      && <div className="pm-status error">{localizeMessage(error)}</div>}

            {loading ? (
              <div className="pm-spinner"><div className="pm-dot-spin" /> {t('postModal.loadingPost')}</div>
            ) : !post ? (
              <div className="pm-spinner">{t('postModal.postNotFound')}</div>
            ) : (
              <>
                {/* Post card */}
                <div style={{ paddingBottom: 0 }}>

                  {/* Header */}
                  <div className="post-header" style={{ display: 'flex', alignItems: 'center', gap: 10, paddingBottom: 12 }}>
                    <img src={getImageUrl(post.author?.profileImage)} alt={post.author?.name}
                      className="post-author-avatar"
                      onClick={() => { onClose(); navigate(`/profile/${post.author?._id}`); }}
                      style={{ cursor: 'pointer', width: 42, height: 42, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                      onError={handleImageError} />
                    <div className="post-author-info" onClick={() => { onClose(); navigate(`/profile/${post.author?._id}`); }}
                      style={{ cursor: 'pointer', flex: 1 }}>
                      <div className="post-author-name" style={{ fontWeight: 600, fontSize: 15 }}>
                        {post.author?.organizationName || post.author?.name || t('postModal.unknown')}
                      </div>
                      <div className="post-meta">
                        <span className="post-time" style={{ fontSize: 12, color: '#65676b' }}>{formatDate(post.createdAt, t)}</span>
                      </div>
                    </div>
                    <PostOptionsInline post={post} userId={userId}
                      onDelete={() => setShowDelPost(true)}
                      onReport={(pId) => { setReportTarget({ type: 'post', id: pId, reason: '' }); setShowReport(true); }} />
                  </div>

                  {/* Content */}
                  {post.content && (
                    <p style={{ margin: '0 0 12px', fontSize: 15, color: '#1c1e21', lineHeight: 1.55, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {post.content}
                    </p>
                  )}

                  {/* Images */}
                  {hasImages && (
                    <div style={{ marginBottom: 12 }}>
                      <ImageCarousel images={post.images} />
                    </div>
                  )}

                  {/* Action buttons */}
                  <div className="pm-actions">
                    <button className={`pm-action-btn ${isLiked() ? 'active' : ''}`} onClick={handleLike} disabled={isLiking}>
                      {isLiking
                        ? <div style={{ width: 18, height: 18, border: '2px solid #65676b', borderTopColor: 'transparent', borderRadius: '50%', animation: 'pmSpin .7s linear infinite' }} />
                        : <Heart size={20} fill={isLiked() ? '#32614d' : 'none'} stroke={isLiked() ? '#32614d' : '#65676b'} />
                      }
                      <span>{post.likes?.length || 0} {post.likes?.length === 1 ? t('postModal.like') : t('postModal.likes')}</span>
                    </button>

                    <button className={`pm-action-btn ${showComments ? 'active' : ''}`} onClick={() => setShowComments(s => !s)}>
                      <MessageCircle size={20} stroke={showComments ? '#32614d' : '#65676b'} />
                      <span>{post.comments?.length || 0} {post.comments?.length === 1 ? t('postModal.comment') : t('postModal.comments')}</span>
                    </button>

                    <button className={`pm-action-btn ${isPostSaved() ? 'active' : ''}`} onClick={handleSave} disabled={isSaving}>
                      {isSaving
                        ? <div style={{ width: 18, height: 18, border: '2px solid #65676b', borderTopColor: 'transparent', borderRadius: '50%', animation: 'pmSpin .7s linear infinite' }} />
                        : <Bookmark size={20} fill={isPostSaved() ? '#32614d' : 'none'} stroke={isPostSaved() ? '#32614d' : '#65676b'} />
                      }
                      <span>{t('postModal.save')}</span>
                    </button>

                    <button className="pm-action-btn"
                      onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/post/${postId}`); setSuccessMsg(t('postModal.linkCopied')); }}>
                      <img src="/nkarner/Screenshot_2025-09-08_200654-removebg-preview (1).png" alt={t('postModal.share')}
                        style={{ width: 20, height: 20, transform: 'rotate(30deg)' }} />
                      <span>{t('postModal.share')}</span>
                    </button>
                  </div>
                </div>

                {/* Comments section */}
                {showComments && (
                  <div style={{ marginTop: 16 }}>
                    <div className="comments-list">
                      {commentTree.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '24px 0', color: '#65676b' }}>
                          <MessageCircle size={28} style={{ color: '#ccc', marginBottom: 8 }} />
                          <p style={{ margin: 0 }}>{t('postModal.noCommentsYet')}</p>
                          <small>{t('postModal.beFirstComment')}</small>
                        </div>
                      ) : renderTree(commentTree)}
                    </div>

                    {/* Comment input */}
                    <div style={{ display: 'flex', gap: 8, marginTop: 16, paddingTop: 12, borderTop: '1px solid #f0f0f0' }}>
                      <div style={{ position: 'relative', flex: 1 }}>
                        <input type="text" placeholder={t('postModal.writeComment')}
                          value={commentInput}
                          onChange={(e) => setCommentInput(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && handleComment()}
                          style={{ width: '100%', padding: '10px 42px 10px 14px', border: '1px solid #e0e0e0', borderRadius: 22, outline: 'none', fontSize: 14, boxSizing: 'border-box' }} />
                        <button onClick={(e) => { e.stopPropagation(); setActiveEmoji(activeEmoji === postId ? null : postId); }}
                          style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#65676b' }}>
                          <Smile size={18} />
                        </button>
                        {activeEmoji === postId && (
                          <div style={{ position: 'absolute', bottom: '110%', left: 0, zIndex: 9999 }}>
                            <EmojiPicker onEmojiClick={(d) => { setCommentInput(t => t + d.emoji); setActiveEmoji(null); }}
                              disableAutoFocus native height={320} width={280} />
                          </div>
                        )}
                      </div>
                      <button onClick={() => handleComment()} disabled={!commentInput.trim() || isCommenting}
                        style={{ background: '#32614d', color: 'white', border: 'none', borderRadius: '50%', width: 38, height: 38, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, opacity: commentInput.trim() && !isCommenting ? 1 : 0.45 }}>
                        {isCommenting
                          ? <div style={{ width: 16, height: 16, border: '2px solid white', borderTopColor: 'transparent', borderRadius: '50%', animation: 'pmSpin .7s linear infinite' }} />
                          : <Send size={16} color="white" />
                        }
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Delete Post Modal */}
      {showDelPost && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }} onClick={() => setShowDelPost(false)}>
          <div style={{ background: 'white', borderRadius: 16, width: '90%', maxWidth: 380, overflow: 'hidden' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #f0f0f0' }}>
              <h3 style={{ margin: 0, fontSize: 18 }}>{t('postModal.deletePost')}</h3>
              <button onClick={() => setShowDelPost(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#65676b' }}><X size={20} /></button>
            </div>
            <div style={{ padding: 20, textAlign: 'center' }}>
              <X size={48} style={{ color: '#ef4444', marginBottom: 12 }} />
              <h4 style={{ margin: '0 0 8px' }}>{t('postModal.areYouSure')}</h4>
              <p style={{ margin: 0, color: '#65676b', fontSize: 14 }}>{t('postModal.postWillBeDeleted')}</p>
            </div>
            <div style={{ display: 'flex', gap: 8, padding: '12px 20px', borderTop: '1px solid #f0f0f0' }}>
              <button onClick={() => setShowDelPost(false)} style={{ flex: 1, padding: 10, borderRadius: 8, border: 'none', background: '#f0f0f0', cursor: 'pointer', fontWeight: 600 }}>{t('postModal.cancel')}</button>
              <button onClick={async () => { await handleDeletePost(); setShowDelPost(false); }} style={{ flex: 1, padding: 10, borderRadius: 8, border: 'none', background: '#ef4444', color: 'white', cursor: 'pointer', fontWeight: 600 }}>{t('postModal.delete')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Comment Modal */}
      {showDelComment && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }} onClick={() => setShowDelComment(false)}>
          <div style={{ background: 'white', borderRadius: 16, width: '90%', maxWidth: 380, overflow: 'hidden' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #f0f0f0' }}>
              <h3 style={{ margin: 0, fontSize: 18 }}>{t('postModal.deleteComment')}</h3>
              <button onClick={() => setShowDelComment(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#65676b' }}><X size={20} /></button>
            </div>
            <div style={{ padding: 20, textAlign: 'center' }}>
              <X size={48} style={{ color: '#ef4444', marginBottom: 12 }} />
              <h4 style={{ margin: 0 }}>{t('postModal.deleteThisComment')}</h4>
            </div>
            <div style={{ display: 'flex', gap: 8, padding: '12px 20px', borderTop: '1px solid #f0f0f0' }}>
              <button onClick={() => setShowDelComment(false)} style={{ flex: 1, padding: 10, borderRadius: 8, border: 'none', background: '#f0f0f0', cursor: 'pointer', fontWeight: 600 }}>{t('postModal.cancel')}</button>
              <button onClick={async () => { await handleDeleteComment(commentToDel); setShowDelComment(false); }} style={{ flex: 1, padding: 10, borderRadius: 8, border: 'none', background: '#ef4444', color: 'white', cursor: 'pointer', fontWeight: 600 }}>{t('postModal.delete')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Report Modal */}
      {showReport && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }} onClick={() => setShowReport(false)}>
          <div style={{ background: 'white', borderRadius: 16, width: '90%', maxWidth: 400, overflow: 'hidden' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #f0f0f0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><AlertTriangle size={18} /><h3 style={{ margin: 0, fontSize: 18 }}>{t('postModal.report')}</h3></div>
              <button onClick={() => setShowReport(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#65676b' }}><X size={20} /></button>
            </div>
            <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[
                { value: 'Spam', label: t('postModal.reasonSpam') },
                { value: 'Inappropriate Content', label: t('postModal.reasonInappropriate') },
                { value: 'Harassment', label: t('postModal.reasonHarassment') },
                { value: 'Hate Speech', label: t('postModal.reasonHateSpeech') },
                { value: 'Misinformation', label: t('postModal.reasonMisinformation') },
                { value: 'Other', label: t('postModal.reasonOther') },
              ].map(({ value, label }) => (
                <div key={value} onClick={() => setReportTarget(prev => ({ ...prev, reason: value }))}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', border: `1px solid ${reportTarget.reason === value ? '#32614d' : '#e0e0e0'}`, borderRadius: 8, cursor: 'pointer', background: reportTarget.reason === value ? '#f0f7f4' : 'white' }}>
                  <div style={{ width: 16, height: 16, border: '2px solid #32614d', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {reportTarget.reason === value && <div style={{ width: 8, height: 8, background: '#32614d', borderRadius: '50%' }} />}
                  </div>
                  <span style={{ fontSize: 14 }}>{label}</span>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, padding: '12px 20px', borderTop: '1px solid #f0f0f0' }}>
              <button onClick={() => setShowReport(false)} style={{ flex: 1, padding: 10, borderRadius: 8, border: 'none', background: '#f0f0f0', cursor: 'pointer', fontWeight: 600 }}>{t('postModal.cancel')}</button>
              <button disabled={!reportTarget.reason} onClick={async () => { await handleReport(reportTarget); setShowReport(false); }}
                style={{ flex: 1, padding: 10, borderRadius: 8, border: 'none', background: reportTarget.reason ? '#32614d' : '#f0f0f0', color: reportTarget.reason ? 'white' : '#65676b', cursor: reportTarget.reason ? 'pointer' : 'not-allowed', fontWeight: 600 }}>
                {t('postModal.submit')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── Inline PostOptions ────────────────────────────────────────────────────
function PostOptionsInline({ post, userId, onDelete, onReport }) {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setShow(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const isOwner = post.author?._id === userId;
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <span onClick={(e) => { e.stopPropagation(); setShow(!show); }} style={{ cursor: 'pointer', color: '#65676b', display: 'flex' }}>
        <MoreVertical size={20} />
      </span>
      {show && (
        <div style={{ position: 'absolute', top: '100%', right: 0, background: 'white', border: '1px solid #e0e0e0', borderRadius: 10, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', zIndex: 200, minWidth: 150, overflow: 'hidden' }}>
          {isOwner && (
            <div onClick={(e) => { e.stopPropagation(); onDelete(); setShow(false); }}
              style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: '#ef4444', fontSize: 14 }}>
              <Trash2 size={15} /><span>{t('postModal.deletePost')}</span>
            </div>
          )}
          <div onClick={(e) => { e.stopPropagation(); onReport(post._id); setShow(false); }}
            style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: '#65676b', fontSize: 14 }}>
            <Flag size={15} /><span>{t('postModal.reportPost')}</span>
          </div>
        </div>
      )}
    </div>
  );
}